import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
  useMemo,
  type TextareaHTMLAttributes,
} from 'react'
import {
  MENTION_POPUP_MAX_HEIGHT,
  MentionPopup,
  filterCandidates,
  type MentionCandidate,
} from './MentionPopup'
import { getActiveMention, insertMention } from '../../utils/mention-detector'
import { useMentionableEmployees } from '../../hooks/useMentionableEmployees'
import { splitMentionTokens } from '../../utils/mention-highlight'
import { useAuth } from '@/contexts/AuthContext'

/** Lista vazia estável — um `[]` novo a cada render refaria os memos de
 * candidatos e de destaque toda vez. */
const NO_CANDIDATES: MentionCandidate[] = []

/** Folga entre o popup e a borda da janela, pra não colar. */
const VIEWPORT_MARGIN = 12

/**
 * Decide de que lado o popup abre e com que altura, a partir do retângulo do
 * campo. Preferência é abrir para cima (padrão de chat); inverte quando não
 * cabe acima e há mais espaço abaixo. Pura pra poder ser testada isolada.
 */
export function resolvePlacement(rect: {
  top: number
  bottom: number
  viewportHeight: number
}): { placement: 'top' | 'bottom'; maxHeight: number } {
  const spaceAbove = Math.max(0, rect.top - VIEWPORT_MARGIN)
  const spaceBelow = Math.max(0, rect.viewportHeight - rect.bottom - VIEWPORT_MARGIN)
  const fitsAbove = spaceAbove >= MENTION_POPUP_MAX_HEIGHT
  const placement: 'top' | 'bottom' =
    fitsAbove || spaceAbove >= spaceBelow ? 'top' : 'bottom'
  const available = placement === 'top' ? spaceAbove : spaceBelow
  return {
    placement,
    maxHeight: Math.max(120, Math.min(MENTION_POPUP_MAX_HEIGHT, available)),
  }
}

interface MentionableTextareaProps
  extends Omit<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    'value' | 'onChange'
  > {
  /** Processo cujos mencionáveis alimentam o `@` (Ocorrências). */
  processId?: number | null
  /**
   * Candidatos já resolvidos por quem chama — usado fora de um processo
   * (ex.: colegas da empresa no formulário de Atendimento HUB). Quando
   * informado, `processId` é ignorado.
   */
  candidates?: MentionCandidate[]
  value: string
  onChange: (value: string) => void
  /** Quando true, Enter envia (chamando onSubmit). Shift+Enter quebra linha. */
  submitOnEnter?: boolean
  onSubmit?: () => void
}

/**
 * Textarea com autocomplete de menções (@) reutilizável. Mostra menções em
 * azul em tempo real via overlay (textarea transparente + div espelho
 * absolutamente posicionado com mesmas classes). Reaproveita o mesmo
 * parser/popup usados no MessageEditor.
 */
export const MentionableTextarea = forwardRef<
  HTMLTextAreaElement,
  MentionableTextareaProps
>(function MentionableTextarea(
  {
    processId = null,
    candidates: candidatesProp,
    value,
    onChange,
    submitOnEnter,
    onSubmit,
    onKeyDown,
    onCompositionStart,
    onCompositionEnd,
    onScroll,
    className,
    ...rest
  },
  externalRef,
) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const mirrorRef = useRef<HTMLDivElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  useImperativeHandle(externalRef, () => textareaRef.current!, [])

  const [composing, setComposing] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const { data: processCandidates = NO_CANDIDATES } = useMentionableEmployees(
    candidatesProp ? null : processId,
  )
  // A lista do processo inclui o próprio usuário (para ele poder ser
  // responsável); no `@` ele não faz sentido — o backend ignora auto-menção.
  const { user } = useAuth()
  const candidates: MentionCandidate[] = useMemo(
    () =>
      (candidatesProp ?? processCandidates).filter(
        (c) => Number(c.id) !== Number(user?.id),
      ),
    [candidatesProp, processCandidates, user?.id],
  )

  const caret = textareaRef.current?.selectionStart ?? value.length
  const mention = getActiveMention(value, caret)
  const filtered = mention ? filterCandidates(candidates, mention.query) : []
  const popupOpen = !!mention && filtered.length > 0

  // Tokens para o overlay — calcula menções válidas (longest-first) e mantém
  // o texto restante como texto plano.
  const tokens = useMemo(
    () =>
      splitMentionTokens(
        value,
        candidates.map((c) => ({ name: c.name })),
      ),
    [value, candidates],
  )

  // Lado/altura do popup recalculados sempre que ele abre ou o campo muda de
  // posição (digitar pode fazer o textarea crescer). `useLayoutEffect` para
  // medir antes da pintura e não piscar do lado errado.
  const [popupBox, setPopupBox] = useState({
    placement: 'top' as 'top' | 'bottom',
    maxHeight: MENTION_POPUP_MAX_HEIGHT,
  })
  useLayoutEffect(() => {
    if (!popupOpen) return
    const el = wrapperRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setPopupBox(
      resolvePlacement({
        top: rect.top,
        bottom: rect.bottom,
        viewportHeight: window.innerHeight,
      }),
    )
  }, [popupOpen, value])

  // Sincroniza scroll do overlay com o textarea (quando ele rola, o espelho
  // deve rolar igual para o overlay não "descolar" visualmente).
  useEffect(() => {
    const el = textareaRef.current
    const mirror = mirrorRef.current
    if (!el || !mirror) return
    mirror.scrollTop = el.scrollTop
    mirror.scrollLeft = el.scrollLeft
  }, [value])

  const handleSelectMention = (employee: MentionCandidate) => {
    if (!mention) return
    const result = insertMention(value, mention, employee.name)
    onChange(result.text)
    setActiveIndex(0)
    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (!el) return
      el.focus()
      el.setSelectionRange(result.caret, result.caret)
    })
  }

  const handleKeyDownInternal = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (composing) return onKeyDown?.(e)
    if (popupOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const chosen = filtered[activeIndex]
        if (chosen) handleSelectMention(chosen)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        if (mention) {
          onChange(
            value.slice(0, mention.startIndex) +
              value.slice(mention.caretIndex),
          )
        }
        return
      }
    }
    if (submitOnEnter && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSubmit?.()
      return
    }
    onKeyDown?.(e)
  }

  const sharedClass = className ?? ''
  // Mirror: usa o mesmo className do textarea para herdar tipografia/padding/
  // borda exatos, mas pinta a borda e o fundo de transparente (a borda real
  // fica no textarea). Whitespace pre-wrap + break-words → mesmo wrapping.
  const mirrorClass = `${sharedClass} pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words !border-transparent !bg-transparent text-body/90`
  // Textarea: igual mas com texto transparente (só o caret aparece) — o que o
  // usuário enxerga é o espelho atrás.
  const textareaClass = `${sharedClass} relative !bg-transparent !text-transparent caret-gray-800`

  return (
    <div className="relative" ref={wrapperRef}>
      {popupOpen && (
        <MentionPopup
          candidates={candidates}
          query={mention!.query}
          activeIndex={activeIndex}
          onHover={setActiveIndex}
          onSelect={handleSelectMention}
          placement={popupBox.placement}
          maxHeight={popupBox.maxHeight}
        />
      )}
      <div ref={mirrorRef} aria-hidden className={mirrorClass}>
        {tokens.map((t, i) =>
          t.type === 'mention' ? (
            <span key={i} className="font-medium text-blue-600 dark:text-blue-400">
              {t.value}
            </span>
          ) : (
            <span key={i}>{t.value}</span>
          ),
        )}
        {/* Newline final precisa de um caractere para o mirror reservar a
            altura da última linha — senão "fecha" antes do textarea. */}
        {value.endsWith('\n') ? '​' : ''}
      </div>
      <textarea
        {...rest}
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDownInternal}
        onCompositionStart={(e) => {
          setComposing(true)
          onCompositionStart?.(e)
        }}
        onCompositionEnd={(e) => {
          setComposing(false)
          onCompositionEnd?.(e)
        }}
        onScroll={(e) => {
          if (mirrorRef.current) {
            mirrorRef.current.scrollTop = e.currentTarget.scrollTop
            mirrorRef.current.scrollLeft = e.currentTarget.scrollLeft
          }
          onScroll?.(e)
        }}
        className={textareaClass}
      />
    </div>
  )
})
